import { useToast } from "primevue/usetoast";

import { useEditManager, useEditStore } from "@/composables/edit-manager";
import { useDesignScope } from "@/composables/use-design-scope";
import { useThreadBranchView } from "@/composables/use-thread-branch-view";
import { useReleaseStore } from "@/stores/release-store";
import { useThreadsStore } from "@/stores/threads-store";
import { useUserManager } from "@/stores/user-store";

const formatShortDate = (date: Date) =>
    date.toLocaleDateString(undefined, { month: "short", day: "numeric" });

/**
 * Wraps edit-store's startEditing() so entering edit mode off-draft silently
 * auto-creates a draft (a kind-less thread, which does not wake the agent)
 * first, and only then flips isEditing — so the very first mutation already
 * carries x-thread-revision and lands on the draft's branch instead of the
 * working copy. Already being on a draft, or a non-threads org, passes
 * straight through to the unchanged direct-edit path.
 *
 * Drafts cover the Design group (Business, Strategy, IT Landscape, Overview),
 * so editing in Build or Run passes straight through — see useDesignScope.
 *
 * Returns whether editing actually started, so callers that go on to write
 * something can stop instead of persisting to the working copy behind a failed
 * draft.
 */
export const useDraftEditing = () => {
    const toast = useToast();
    const { startEditing } = useEditStore();
    const { canEdit } = useEditManager();
    const threadsStore = useThreadsStore();
    const releaseStore = useReleaseStore();
    const { threadsEnabled, user } = useUserManager();
    const { viewingThreadBranch, branchThread, enterDraft } =
        useThreadBranchView();

    // Module-scope-free in-flight latch. A ref would work too, but this is
    // never rendered — it only has to stop a second concurrent call.
    let isStarting = false;
    const { inDesignScope } = useDesignScope();

    /**
     * Whether the draft already in view is one we can just keep editing.
     *
     * It has to mean an OPEN draft: activeThreadId outlives the draft, and a
     * resolved thread whose ?thread= is still on the URL would otherwise
     * short-circuit here with every write going out at a branch that no
     * longer exists. Reads fall back to master, so nothing would look wrong.
     *
     * But "no record yet" is not "no draft". `branchThread` fills from
     * fetchThreads, and list_threads diffs every open draft against master —
     * seconds on a stek with a pile of them. Reading a missing record as
     * "not open" made an Edit click in that window start a SECOND draft and
     * push its id over the one the user was looking at. So ask for the record
     * rather than guessing, and let an unreadable one mean "keep the draft".
     */
    const onOpenDraft = async (): Promise<boolean> => {
        const known = branchThread.value;
        if (known) return known.status === "open";

        const id = releaseStore.activeThreadId;
        if (!id) return false;
        // Status is the only field read here, and this sits between the Edit
        // click and edit mode — so no branch diff: asking for the footprint
        // put a whole worktree build plus a stek-name read in front of the
        // user on a path whose entire job is to be quicker than a listing.
        const fresh = await threadsStore.fetchThreadSummary(id, {
            withFootprint: false,
        });
        return !fresh || fresh.status === "open";
    };

    const beginEditing = async (): Promise<boolean> => {
        // Two rapid clicks used to fire two createThread calls, leaving a
        // second draft and a second branch orphaned on the spot. The latch
        // covers the status read too, which now precedes the create.
        if (isStarting) return false;

        if (!threadsEnabled.value || !canEdit.value || !inDesignScope.value) {
            await startEditing();
            return true;
        }

        isStarting = true;
        try {
            if (viewingThreadBranch.value && (await onOpenDraft())) {
                await startEditing();
                return true;
            }
            return await createAndEnterDraft();
        } finally {
            isStarting = false;
        }
    };

    const createAndEnterDraft = async (): Promise<boolean> => {
        const title = `Draft by ${user.value?.name ?? "you"} — ${formatShortDate(new Date())}`;
        // createThread can reject outright: the shared api client is built with
        // `throwOnError: true`, so a non-2xx never reaches the check below.
        // Without the catch the rejection propagates into callers that are
        // mid-upload, and they abort with no draft, no write and no message.
        let thread: Awaited<ReturnType<typeof threadsStore.createThread>>;
        try {
            // Marked auto-created ON THE THREAD, because this is the one path
            // where the user did not ask for a draft — they clicked Edit and
            // needed somewhere for the change to go. That is what lets leaving
            // erase it again if nothing went into it; see Thread.auto_created.
            thread = await threadsStore.createThread(title, {
                autoCreated: true,
            });
        } catch {
            thread = undefined;
        }
        if (!thread?.branch) {
            toast.add({
                severity: "error",
                summary: "Could not start a draft",
                detail: "Please try again.",
                life: 5000,
            });
            return false;
        }

        await enterDraft(thread.identity);
        await startEditing();
        return true;
    };

    return { beginEditing };
};
